import { Box, Button, Grid2, Typography } from '@mui/material'
import AlertTile from '../components/shared/AlertTile'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store/store';
import { getAreaViewTiles, selectAreaViewTiles, selectIfAreaTilesLoading, selectSiteRatingByArea } from '../store/slices/emergingAlertsSlice';
import GradientBorderedButton from '../components/styled/GradientBorderedButton';
import GradientFilledButton from '../components/styled/GradientFilledButton';
import CheckIcon from '@mui/icons-material/Check';
import { GET_ESCALATION_COLOR } from '../constants/SeverityColors';
import DarkAdnocLoadingProgress from '../components/shared/DarkAdnocLoadingProgress';
import { APP_ROUTES } from '../constants/AppRoutes';

const default_rating_filters = ['high', 'high-medium', 'medium', 'low']

export default function DeviationRadarSitesView() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const { area } = useParams();

	const isAreaTilesLoading = useSelector(selectIfAreaTilesLoading);
	const areaRating = useSelector(selectSiteRatingByArea(area ?? ''));
	const allTiles = useSelector(selectAreaViewTiles(area ?? ''), shallowEqual)

	const [tileViewCount, setTileViewCount] = useState(12);
	const [ratingFiltersSelected, setRatingFiltersSelected] = useState([...default_rating_filters])

	const handleViewAllAreasClick = () => {
		setTileViewCount(prev => prev > 12 ? 12 : allTiles.length)
	}

	const handleRatingFilter = (rating: string) => {
		if (ratingFiltersSelected.includes(rating))
			setRatingFiltersSelected(prev => [...prev.filter((s) => s !== rating)])
		else
			setRatingFiltersSelected(prev => [...prev, rating])
	}

	const getTilesByRating = (tiles: any[], rating: string) => {
		return tiles.filter((s: any) => s.area_escalation_rating.toLowerCase() === rating);
	}

	const sortedByRatingTiles = useMemo(() => {
		let result: any[] = [];
		if (ratingFiltersSelected.includes('high'))
			result = [...result, ...getTilesByRating(allTiles, "high")]
		if (ratingFiltersSelected.includes('high-medium'))
			result = [...result, ...getTilesByRating(allTiles, "high-medium")]
		if (ratingFiltersSelected.includes('medium'))
			result = [...result, ...getTilesByRating(allTiles, "medium")]
		if (ratingFiltersSelected.includes('low'))
			result = [...result, ...getTilesByRating(allTiles, "low")]
		return result.slice(0, tileViewCount)
	}, [allTiles, ratingFiltersSelected, tileViewCount])

	const tilesCountByRatings = useMemo(() => {
		return {
			'high': getTilesByRating(sortedByRatingTiles, 'high').length,
			'high-medium': getTilesByRating(sortedByRatingTiles, 'high-medium').length,
			'medium': getTilesByRating(sortedByRatingTiles, 'medium').length,
			'low': getTilesByRating(sortedByRatingTiles, 'low').length,
		}
	}, [sortedByRatingTiles])

	useEffect(() => {
		if (isAreaTilesLoading === null)
			dispatch(getAreaViewTiles())
	}, [dispatch, isAreaTilesLoading])

	return (
		<Grid2
			sx={{
				width: '100vw',
				height: '120vh',
				boxSizing: 'border-box',
				display: 'grid',
				gridTemplateColumns: '2fr 1fr',
				gridAutoRows: '5vw 20vw',
				position: 'relative',
				top: 'max(64px, 6vh)',
				zIndex: 1,
				margin: 2,
				rowGap: 5,
			}}
		>
			<Box sx={{ gridColumn: '1/-1', width: '66.7vw' }}>
				<Box sx={{
					display: 'grid',
					gridTemplateColumns: 'auto 0.5fr',
					columnGap: 10,
					paddingY: 1,
				}}>
					<Box sx={{
						background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) -7.34%, rgba(255, 255, 255, 0.52) 50%)',
						paddingY: 1,
					}}>
						<Typography
							variant='h4'
							sx={{
								borderLeft: `0.5vw solid ${GET_ESCALATION_COLOR(areaRating.site_rating)}`,
								paddingX: 1,
								lineHeight: 1,
								marginRight: '25%',
							}}>
							{area}
						</Typography>
					</Box>
					<Button variant='text'
						onClick={handleViewAllAreasClick}
						sx={{
							width: 'max-content',
							fontWeight: 700,
							color: '#00A9B1',
							textDecoration: 'underline',
							padding: 0,
						}}>
						{sortedByRatingTiles.length > 12 ? "Collapse all areas" : "View all areas"}
					</Button>
				</Box>
				<Box style={{ display: 'flex', gap: '0.75vw', alignItems: 'center', marginBottom: '1vh' }}>
					<NavLink to={APP_ROUTES.DEVIATION_RADAR} className='masthead_link' style={{ color: '#00A9B1' }} >
						Deviation Radar
					</NavLink>
					<GradientBorderedButton sx={{
						paddingX: '1vw',
						borderRadius: '1vw !important'
					}}
						onClick={() => setRatingFiltersSelected([...default_rating_filters])}>
						Show Escalation Priorities
					</GradientBorderedButton>
					{
						Object.keys(tilesCountByRatings).map((key) => {
							return <GradientFilledButton
								key={key}
								sx={{
									borderRadius: '1vw !important',
									paddingX: '1vw',
									paddingY: '0.25vh',
								}}
								startIcon={(ratingFiltersSelected.includes(key) ? <CheckIcon /> : <></>)}
								onClick={() => handleRatingFilter(key)}>
								{
									<Typography textTransform={'capitalize'}>
										{`${key} (${tilesCountByRatings[key as keyof typeof tilesCountByRatings]})`}
									</Typography>
								}
							</GradientFilledButton>
						})
					}
				</Box>
			</Box>
			{
				isAreaTilesLoading === true && <Box sx={{
					width: '100%',
					textAlign: 'center'
				}}>
					<DarkAdnocLoadingProgress size={'xlarge'}></DarkAdnocLoadingProgress>
				</Box>
			}
			{
				isAreaTilesLoading === false && sortedByRatingTiles && sortedByRatingTiles.length === 0 &&
				<Box sx={{
					width: '100%',
					textAlign: 'center'
				}}>
					<Typography>No data found.</Typography>
				</Box>
			}
			<Grid2
				sx={{
					display: 'grid',
					justifyContent: 'space-around',
					gridTemplateColumns: 'repeat(3, 1fr)',
					gap: 5,
					placeItems: 'center',
					width: '95%',
				}}
			>
				{sortedByRatingTiles.length > 0 &&
					sortedByRatingTiles.map((alert: any, index: number) => {
						const queryParams = `areaDescription=${alert.area_description}&areaPriority=${alert.area_escalation_rating}`
						return (
							<Box
								onClick={() => navigate(`${APP_ROUTES.DEVIATION_RADAR}/${area}/${alert.area_code}?${queryParams}`)}
								key={index}
								sx={{
									opacity: 1,
									transition: 'scale 200ms ease-in-out',
									'&:hover': {
										scale: 1.05,
										cursor: 'pointer',
									}
								}}>
								<AlertTile
									key={index}
									code={alert.area_code}
									label={alert.area_description}
									severity={alert.area_escalation_rating}
								></AlertTile>
							</Box>
						);
					})}
			</Grid2>
		</Grid2>
	);
}
